import { ArrowRight, RefreshCcw } from 'lucide-react';
import { Button } from '@/components/ui';
import type { WorkflowNextEvent } from '../types/workflow';

interface NextActionsPanelProps {
  events: WorkflowNextEvent[];
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
  onSelect: (event: WorkflowNextEvent) => void;
  onRefresh: () => void;
}

const labelForEvent = (event: WorkflowNextEvent): string => {
  return event.display_title || event.event;
};

export const NextActionsPanel = ({
  events,
  isLoading,
  isFetching,
  error,
  onSelect,
  onRefresh,
}: NextActionsPanelProps) => {
  return (
    <div className="card">
      <div className="card-header flex items-center justify-between">
        <h3 className="text-base font-semibold text-rgf-gray-900">Next Actions</h3>
        <Button
          variant="ghost"
          size="sm"
          icon={<RefreshCcw className="h-4 w-4" />}
          onClick={onRefresh}
          disabled={isFetching}
        >
          Refresh
        </Button>
      </div>
      <div className="card-body">
        {isLoading ? (
          <div className="text-sm text-rgf-gray-500">Loading available actions…</div>
        ) : error ? (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            {error.message || 'Failed to load workflow state.'}
          </div>
        ) : events.length === 0 ? (
          <div className="text-sm text-rgf-gray-500">
            No actions are available to you for this candidate at this stage.
          </div>
        ) : (
          <ul className="space-y-2">
            {events.map((event) => (
              <li key={event.event}>
                <button
                  type="button"
                  className="w-full flex items-center justify-between rounded-lg border border-rgf-gray-200 px-4 py-3 text-left hover:border-rgf-primary-400 hover:bg-rgf-primary-50 transition-colors"
                  onClick={() => onSelect(event)}
                >
                  <div>
                    <div className="text-sm font-medium text-rgf-gray-900">{labelForEvent(event)}</div>
                    {event.resource_name && (
                      <div className="text-xs text-rgf-gray-500 font-mono">{event.resource_name}</div>
                    )}
                  </div>
                  <ArrowRight className="h-4 w-4 text-rgf-gray-400" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};
